import { BaseballCommonLineupPositions, BaseballLineupPosition, PlayerSearchService } from '@trackman/baseball-shared-components';
import { Ruleset } from 'enums/Ruleset';
import { StrikeZoneDefinitionEnum } from 'interfaces/session/IStrikeZoneDefinition';
import { IApiSessionTeam } from 'interfaces/tagging/ApiModels/IApiSessionMetadataUpdateRequest';
import { ApiLineupDraggablePlayer } from 'interfaces/team/ApiLineupDraggablePlayer';
import { IPlayer } from 'interfaces/team/IPlayer';
import type { ITeam } from 'interfaces/team/ITeam';
import { action, computed, IObservableArray, makeObservable, observable } from 'mobx';
import { lineupEntryToApiLineupPlayerMap } from 'services/helper-services/mappingHelpers/playerMappings';
import { v1 as uuid } from 'uuid';
import { ILineupEntry } from '../../interfaces/team/ILineupEntry';

export class LineupManager {
  constructor(
    team: ITeam,
    ruleset: Ruleset,
    roster: IPlayer[],
    activeStrikeZone: StrikeZoneDefinitionEnum = StrikeZoneDefinitionEnum.Static,
    maxPlayers: number,
    oldLineup?: ILineupEntry[],
    allowForeignIdOnPlaceholderPlayers?: boolean,
    allowSamePlayerOnBothTeams?: boolean
  ) {
    this.team = team;
    this.roster = roster.sort((a, b) => a.lastName.localeCompare(b.lastName));
    this.ruleset = ruleset;
    this.searchService = new PlayerSearchService(this.roster);
    this.lineup =
      oldLineup?.map((i) => ({ ...i, isEnabled: true })) ||
      observable.array(this.createEmptyLineup(ruleset)).map((i) => ({ ...i, isEnabled: true }));
    this.allowForeignIdsOnPlaceholderPlayers = !!allowForeignIdOnPlaceholderPlayers;
    this.allowSamePlayerOnBothTeams = !!allowSamePlayerOnBothTeams;
    this.maxPlayers = maxPlayers;
    this.onLineupPlayerAdded();
    this.id = uuid();
    this.activeStrikeZone = activeStrikeZone;
    makeObservable(this);
  }

  @observable activeStrikeZone: StrikeZoneDefinitionEnum;

  static ScrimmageMaxPlayers = 16;

  id: string;

  @observable hasEntryChanged: string = uuid();

  @observable team: ITeam;

  @observable ruleset: Ruleset;

  @observable allowForeignIdsOnPlaceholderPlayers: boolean;

  @observable allowSamePlayerOnBothTeams: boolean;

  @observable roster: IPlayer[];

  @observable lineup: ILineupEntry[];

  @observable searchService: PlayerSearchService;

  @observable maxPlayers: number;

  @computed({ keepAlive: true }) get lineupPlayers() {
    return this.lineup;
  }

  @computed({ keepAlive: true }) get playersWithMissingSZ(): IPlayer[] {
    const players: IPlayer[] = [];

    this.lineup.forEach((item: ILineupEntry) => {
      if (!item.player) {
        return;
      }

      switch (this.activeStrikeZone) {
        case StrikeZoneDefinitionEnum.HeightBased: {
          if (!item.player.height) {
            players.push(item.player);
          }
          break;
        }
        case StrikeZoneDefinitionEnum.TopAndBottomBased: {
          if (!item.player.strikeZoneBottom || !item.player.strikeZoneTop) {
            players.push(item.player);
          }
          break;
        }
        case StrikeZoneDefinitionEnum.Static:
        default:
          break;
      }
    });

    return players;
  }

  @computed({ keepAlive: true }) get maxLineupEntries(): number {
    return this.getRequiredPositions().length;
  }

  @computed({ keepAlive: true }) get isBattingPractice(): boolean {
    return this.ruleset === Ruleset.battingPractice;
  }

  @computed({ keepAlive: true }) get isScrimmage(): boolean {
    return this.ruleset === Ruleset.scrimmage;
  }

  @computed({ keepAlive: true }) get isValidated(): boolean {
    if (this.isScrimmage) {
      return this.lineup.some((e) => e.player && e.position === BaseballLineupPosition.Pitcher);
    }
    if (!this.isBattingPractice) {
      const filledPositions = this.getFilledPositions();

      const noDuplicatesExceptDH = filledPositions.filter((position, index, arr) => {
        if (position === BaseballLineupPosition.DesignatedHitter) {
          return true;
        }

        return arr.indexOf(position) === index;
      });

      const isAllPositionFilled = this.isAllPositionFilled(noDuplicatesExceptDH);

      return this.lineup.length === noDuplicatesExceptDH.length && this.lineup.length <= this.maxPlayers && isAllPositionFilled;
    }
    return this.lineup.some((e) => e.player);
  }

  @computed({ keepAlive: true }) get isAddDesignatedHitterEnabled(): boolean {
    return this.ruleset === Ruleset.americanLeague && this.lineup.length < this.maxPlayers && this.maxPlayers > 10;
  }

  @computed({ keepAlive: true }) get isAddDesignatedHitterVisible(): boolean {
    return this.ruleset === Ruleset.americanLeague && this.maxPlayers > 10;
  }

  private isAllPositionFilled = (filledPositions: string[]) => {
    const isFilled: boolean[] = [];
    Object.values(BaseballLineupPosition).forEach((position) => {
      if (!this.isAddDesignatedHitterEnabled && position === BaseballLineupPosition.DesignatedHitter) {
        return;
      }
      if (position === BaseballLineupPosition.Undefined) {
        return;
      }
      isFilled.push(filledPositions.includes(position));
    });

    return !isFilled.includes(false);
  };

  private createEmptyLineup = (ruleset: Ruleset): ILineupEntry[] => {
    const res = [] as ILineupEntry[];
    if (ruleset === Ruleset.battingPractice || ruleset === Ruleset.scrimmage) {
      const id = uuid();
      res.push({ order: 1, id: id, isEnabled: true, name: id });
      return res;
    }
    for (let i = 0; i < 9; i++) {
      const id = uuid();

      res.push({ order: i, id: id, isEnabled: true, name: id });
    }
    if (ruleset === Ruleset.americanLeague) {
      const id = uuid();

      res.push({ position: BaseballLineupPosition.Pitcher, id: 'P', isEnabled: true, name: id });
    }
    return res;
  };

  @action autoFillLineUp = (lastLineup: ILineupEntry[]): void => {
    const positions = this.getRequiredPositions() || [];

    for (let i = 0; i < positions.length; i++) {
      const lineupEntry = lastLineup.find((l) => l.position === positions[i]);

      if (lineupEntry) {
        const playerInRoster = this.roster.find((p) => p.id === lineupEntry.id);
        if (playerInRoster) {
          this.addPlayerToLineup(playerInRoster, positions[i]);
        }
      }
    }
  };

  @action updateLineupOrder = (newLineup: ILineupEntry[]): void => {
    (this.lineup as IObservableArray<ILineupEntry>).replace(newLineup);
  };

  @action updateEntry = (oldEntry: ILineupEntry, newEntry?: ILineupEntry): void => {
    this.hasEntryChanged = uuid();

    if (oldEntry.isDesignatedHitter && !newEntry) {
      const index = this.lineup.findIndex((p) => p.id === oldEntry.id);
      this.lineup.splice(index, 1);
      return;
    }

    if (!newEntry) {
      oldEntry.player = undefined;
      if (oldEntry.position !== BaseballLineupPosition.Pitcher || this.ruleset !== Ruleset.americanLeague) {
        // don't clear position if it's a sticky Pitcher position
        oldEntry.position = undefined;
      }
      // due to some undisclosed reason MobX doesn't remember position for additional DHs, so we need to repopulate it
      if (oldEntry.isDesignatedHitter) oldEntry.position = BaseballLineupPosition.DesignatedHitter;
      this.onLineupPlayerRemoved();
    } else {
      oldEntry.order = newEntry.order;
      oldEntry.player = newEntry.player;
      oldEntry.position = newEntry.position;
      this.onLineupPlayerAdded();
    }
  };

  @action addPlayerToRoster = (player: IPlayer): void => {
    this.roster.push(player);
    this.searchService = new PlayerSearchService(this.roster);
  };

  @action addPlayerToLineup = (player: IPlayer, position?: BaseballLineupPosition): void => {
    if (this.ruleset === Ruleset.americanLeague && position === BaseballLineupPosition.Pitcher) {
      const lineupEntry = this.lineup.find((p) => p.position === BaseballLineupPosition.Pitcher);
      if (lineupEntry && !lineupEntry.player) {
        this.updateEntry(lineupEntry, {
          ...lineupEntry,
          player,
          position,
        });
      }
      return;
    }

    for (let i = 0; i < this.lineup.length; i++) {
      if (this.lineup[i].player === undefined) {
        this.updateEntry(this.lineup[i], {
          ...this.lineup[i],
          player,
          position: this.getValidPosition(this.lineup[i].position, position),
        });
        return;
      }
    }
  };

  private getValidPosition = (lineupPosition?: BaseballLineupPosition, position?: BaseballLineupPosition) => {
    if (lineupPosition === BaseballLineupPosition.Pitcher) {
      return lineupPosition;
    }

    return position || lineupPosition;
  };

  @action addDesignatedHitter = (): void => {
    const id = uuid();
    this.lineup.push({
      id: id,
      player: undefined,
      order: this.lineup.length + 1,
      position: BaseballLineupPosition.DesignatedHitter,
      isDesignatedHitter: true,
      isEnabled: true,
      name: id,
    });
  };

  @action removePlayerFromLineup = (player: IPlayer): void => {
    if (!player) {
      return;
    }
    const entry = this.lineup.find((x) => !!x.player && x.player.id === player.id);
    if (entry) {
      this.updateEntry(entry, undefined);
    }
  };

  @action onLineupPlayerAdded = (): void => {
    if (this.isBattingPractice) {
      if (this.lineup.every((entry) => !!entry.player)) {
        const id = uuid();
        if (this.lineup && this.lineup.length > 0 && typeof this.lineup !== 'undefined') {
          this.lineup.push({
            id: id,
            order: this.lineup[this.lineup.length - 1].order! + 1,
            isEnabled: true,
            name: id,
          });
        } else {
          this.lineup.push({
            id: id,
            order: 0,
            isEnabled: true,
            name: id,
          });
        }
      }
    }
    if (this.isScrimmage) {
      if (this.lineup.every((entry) => !!entry.player) && this.lineup.length < LineupManager.ScrimmageMaxPlayers) {
        const id = uuid();
        this.lineup.push({
          id: id,
          order: this.lineup[this.lineup.length - 1].order! + 1,
          isEnabled: true,
          name: id,
        });
      }
    }
  };

  @action onLineupPlayerRemoved = (): void => {
    if (this.isBattingPractice || this.isScrimmage) {
      const idx = this.lineup.findIndex((e) => !e.player);
      this.lineup.splice(idx, 1);
      for (let i = 0; i < this.lineup.length; i++) {
        this.lineup[i].order = i + 1;
      }
    }
  };

  public getRequiredPositions = (): BaseballLineupPosition[] => {
    if (this.isScrimmage) {
      return [BaseballLineupPosition.Pitcher];
    }
    const positions = [...BaseballCommonLineupPositions];
    if (this.lineup.length > 9) {
      positions.push(BaseballLineupPosition.DesignatedHitter);
    }

    return positions;
  };

  public getSelectablePositions = (): BaseballLineupPosition[] => {
    if (this.isScrimmage) {
      return [...BaseballCommonLineupPositions, BaseballLineupPosition.DesignatedHitter];
    }
    const requiredPositions = this.getRequiredPositions();
    if (requiredPositions.some((p) => p === BaseballLineupPosition.DesignatedHitter)) {
      return requiredPositions.filter((p) => p !== BaseballLineupPosition.Pitcher);
    }
    return requiredPositions;
  };

  public getFilledPositions = (): string[] => {
    return this.lineup.filter((entry) => !!entry.position && entry.player).map((entry) => entry.position as string);
  };

  public getUnavailablePlayerIds = (position?: string): string[] => {
    const result = this.lineup
      .filter((entry) => {
        if (position === BaseballLineupPosition.Pitcher || this.isScrimmage) {
          return (
            !!entry.player &&
            entry.position !== BaseballLineupPosition.Pitcher &&
            entry.position !== BaseballLineupPosition.DesignatedHitter
          );
        }
        return !!entry.player && entry.position !== BaseballLineupPosition.Pitcher;
      })
      .map((entry) => entry.player!.id);

    return result;
  };

  public getUnavailablePlayerIdsForOpposingTeam = (): string[] => {
    return this.allowSamePlayerOnBothTeams ? [] : this.getUnavailablePlayerIds();
  };

  public isPitcherAllowedToBat = (): boolean => {
    return !(this.ruleset === Ruleset.americanLeague || (this.ruleset === Ruleset.scrimmage && this.lineup.length > 9));
  };

  public getIndexLabel = (arrayIndex: number): string => (arrayIndex + 1).toString();

  public isPlayerInLineup = (player: IPlayer): boolean => {
    return !!this.lineup.find((p) => p.player?.id === player.id);
  };

  public getMetadataTeam = (): IApiSessionTeam & { roster: IPlayer[] } => {
    return {
      id: this.team.id,
      name: this.team.name,
      shortName: this.team.shortName,
      externalId: this.team.externalId,
      externalIds: this.team.externalIds,
      lineup: this.getMetadataLineup(),
      roster: this.roster,
      organizationId: this.team.organizationId,
    };
  };

  public getMetadataLineup = (): ApiLineupDraggablePlayer[] => {
    return this.lineup.filter((e) => !!e.player).map((entry) => lineupEntryToApiLineupPlayerMap(entry));
  };
}

export default LineupManager;
